<!-- register.component.html -->
<div class="h-screen flex overflow-hidden">
  <!-- Left Side - Reusable Component -->
  <div class="hidden lg:flex lg:w-1/2 lg:flex-shrink-0">
    <div class="w-full overflow-y-auto">
      <app-auth-side-panel></app-auth-side-panel>
    </div>
  </div>

  <!-- Right Side - Register Form -->
  <div class="flex-1 overflow-y-auto bg-gradient-to-br from-white to-violet-50">
    <div class="min-h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div class="max-w-md w-full space-y-8 py-12">
        <!-- Sign Up Header -->
        <div>
          <div class="flex justify-center mb-8">
            <img class="h-12 w-auto" src="/assets/logo-default.svg" alt="SalesOMMO">
          </div>
          <h2 class="text-2xl font-bold text-gray-900 text-center">Create your account</h2>
        </div>

        <!-- Register Form -->
        <form [formGroup]="form" (submit)="processRegister()" method="POST">
          <fieldset [disabled]="isLoading" class="space-y-6">
            <!-- Name Field -->
            <div>
              <div class="flex justify-start">
                <label for="name" class="block text-sm font-medium leading-6 text-gray-900">Your name</label>
                <span class="font-semibold text-red-500 pl-1">*</span>
              </div>
              <div class="mt-1">
                <input id="name" formControlName="name" type="text" autocomplete="name" required class="input">
              </div>
              <div class="pt-3" *ngIf="form.get('name')?.touched && form.get('name')?.invalid">
                <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('name')?.errors?.['required']">
                  Name is required.
                </div>
              </div>
            </div>

            <!-- Email Field -->
            <div>
              <div class="flex justify-start">
                <label for="email" class="block text-sm font-medium leading-6 text-gray-900">Email address</label>
                <span class="font-semibold text-red-500 pl-1">*</span>
              </div>
              <div class="mt-1">
                <input id="email" formControlName="email" type="email" autocomplete="email" required class="input">
              </div>
              <div class="pt-3" *ngIf="form.get('email')?.touched && form.get('email')?.invalid">
                <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('email')?.errors?.['required']">
                  Email is required.
                </div>
                <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('email')?.errors?.['email'] || form.get('email')?.errors?.['invalidEmail']">
                  Please enter a valid email address with a proper domain
                </div>
              </div>
            </div>

            <!-- LinkedIn URL Field -->
            <div>
              <div class="flex justify-start">
                <label for="linkedin_url" class="block text-sm font-medium leading-6 text-gray-900">LinkedIn URL</label>
                <span class="font-semibold text-red-500 pl-1">*</span>
              </div>
              <div class="mt-1">
                <input id="linkedin_url" formControlName="linkedin_url" type="text" autocomplete="off" required class="input">
              </div>
              <div class="pt-3" *ngIf="form.get('linkedin_url')?.touched && form.get('linkedin_url')?.invalid">
                <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('linkedin_url')?.errors?.['required']">
                  LinkedIn URL is required.
                </div>
                <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('linkedin_url')?.errors?.['pattern']">
                  Please enter a valid LinkedIn URL.
                </div>
              </div>
            </div>

            <!-- Country Field -->
            <div>
              <div class="flex justify-start">
                <label for="country_id" class="block text-sm font-medium leading-6 text-gray-900">Country</label>
                <span class="font-semibold text-red-500 pl-1">*</span>
              </div>
              <select id="country_id" name="country_id" formControlName="country_id" required class="input mt-1">
                <option value="" selected>Select country</option>
                <option *ngFor="let country of countries" [value]="country.id">{{ country.name }}</option>
              </select>
              <div class="pt-3" *ngIf="form.get('country_id')?.touched && form.get('country_id')?.invalid">
                <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('country_id')?.errors?.['required']">
                  Country is required.
                </div>
              </div>
            </div>

            <!-- Company Toggle -->
            <div>
              <div class="flex items-center">
                <label for="toggle-example" class="flex items-center cursor-pointer relative mb-2">
                  <input type="checkbox" (click)="toggleCompany()" id="toggle-example" class="sr-only">
                  <div class="toggle-bg bg-gray-200 border-2 border-gray-200 h-6 w-11 rounded-full"></div>
                  <div class="text-sm font-medium leading-6 text-gray-900 ml-3">Company</div>
                </label>
              </div>
              <span class="text-xs text-gray-500">If you need to be invoiced as a company, toggle this option.</span>
            </div>

            <!-- Company Fields -->
            <div *ngIf="isCompany" class="space-y-6 py-4 bg-midnight-purple/5 p-3 rounded-lg">
              <!-- Company Name -->
              <div>
                <div class="flex justify-start">
                  <label for="company_name" class="block text-sm font-medium leading-6 text-gray-900">Company Name</label>
                  <span class="font-semibold text-red-500 pl-1">*</span>
                </div>
                <div class="mt-1">
                  <input id="company_name" formControlName="company_name" type="text" [required]="isCompany" class="input">
                </div>
                <div class="pt-3" *ngIf="form.get('company_name')?.touched && form.get('company_name')?.invalid">
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('company_name')?.errors?.['required']">
                    Company name is required.
                  </div>
                </div>
              </div>

              <!-- Company VAT -->
              <div>
                <div class="flex justify-start">
                  <label for="company_vat" class="block text-sm font-medium leading-6 text-gray-900">Company Identifier <span class="italic">(VAT Code)</span></label>
                  <span class="font-semibold text-red-500 pl-1">*</span>
                </div>
                <div class="mt-1">
                  <input id="company_vat" formControlName="company_vat" type="text" [required]="isCompany" class="input">
                </div>
                <div class="pt-3" *ngIf="form.get('company_vat')?.touched && form.get('company_vat')?.invalid">
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('company_vat')?.errors?.['required']">
                    Company VAT is required.
                  </div>
                </div>
              </div>

              <!-- Company Country and State/County -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="flex justify-start">
                    <label for="company_country_id" class="block text-sm font-medium leading-6 text-gray-900">Country</label>
                    <span class="font-semibold text-red-500 pl-1">*</span>
                  </div>
                  <select id="company_country_id" name="company_country_id" formControlName="company_country_id" [required]="isCompany" class="input mt-1">
                    <option value="" selected>Select country</option>
                    <option *ngFor="let country of countries" [value]="country.id">{{ country.name }}</option>
                  </select>
                  <div class="pt-3" *ngIf="form.get('company_country_id')?.touched && form.get('company_country_id')?.invalid">
                    <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('company_country_id')?.errors?.['required']">
                      Company country is required.
                    </div>
                  </div>
                </div>

                <div>
                  <div class="flex justify-start">
                    <label for="company_county" class="block text-sm font-medium leading-6 text-gray-900">State / County</label>
                    <span class="font-semibold text-red-500 pl-1">*</span>
                  </div>
                  <div class="mt-1">
                    <input id="company_county" formControlName="company_county" type="text" [required]="isCompany" class="input">
                  </div>
                  <div class="pt-3" *ngIf="form.get('company_county')?.touched && form.get('company_county')?.invalid">
                    <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('company_county')?.errors?.['required']">
                      Company county is required.
                    </div>
                  </div>
                </div>
              </div>

              <!-- City and Zip Code -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="flex justify-start">
                    <label for="company_town" class="block text-sm font-medium leading-6 text-gray-900">City</label>
                    <span class="font-semibold text-red-500 pl-1">*</span>
                  </div>
                  <div class="mt-1">
                    <input id="company_town" formControlName="company_town" type="text" [required]="isCompany" class="input">
                  </div>
                  <div class="pt-3" *ngIf="form.get('company_town')?.touched && form.get('company_town')?.invalid">
                    <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('company_town')?.errors?.['required']">
                      Company town is required.
                    </div>
                  </div>
                </div>

                <div>
                  <div class="flex justify-start">
                    <label for="company_zip_code" class="block text-sm font-medium leading-6 text-gray-900">Zip Code</label>
                    <span class="font-semibold text-red-500 pl-1">*</span>
                  </div>
                  <div class="mt-1">
                    <input id="company_zip_code" formControlName="company_zip_code" type="text" [required]="isCompany" class="input">
                  </div>
                  <div class="pt-3" *ngIf="form.get('company_zip_code')?.touched && form.get('company_zip_code')?.invalid">
                    <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('company_zip_code')?.errors?.['required']">
                      Company zip code is required.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Company Address -->
              <div>
                <div class="flex justify-start">
                  <label for="company_address" class="block text-sm font-medium leading-6 text-gray-900">Address</label>
                  <span class="font-semibold text-red-500 pl-1">*</span>
                </div>
                <div class="mt-1">
                  <input id="company_address" formControlName="company_address" type="text" [required]="isCompany" class="input">
                </div>
                <div class="pt-3" *ngIf="form.get('company_address')?.touched && form.get('company_address')?.invalid">
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('company_address')?.errors?.['required']">
                    Company address is required.
                  </div>
                </div>
              </div>

              <!-- VAT Payer Toggle -->
              <div>
                <div class="flex items-center">
                  <label for="toggle-vat-payer" class="flex items-center cursor-pointer relative mb-2">
                    <input type="checkbox" formControlName="company_is_vat_payer" id="toggle-vat-payer" class="sr-only">
                    <div class="toggle-bg bg-gray-200 border-2 border-gray-200 h-6 w-11 rounded-full"></div>
                    <div class="text-sm font-medium leading-6 text-gray-900 ml-3">VAT Payer</div>
                  </label>
                </div>
                <span class="text-xs text-gray-500">Toggle this button if you are a VAT payer in EU.</span>
              </div>
            </div>

            <!-- Password Field -->
            <div>
              <div class="flex items-center justify-between">
                <div class="flex justify-start">
                  <label for="password" class="block text-sm font-medium leading-6 text-gray-900">Password</label>
                  <span class="font-semibold text-red-500 pl-1">*</span>
                </div>
              </div>
              <div class="mt-1">
                <input id="password" formControlName="password" type="password" autocomplete="new-password" required class="input">
                <div class="pt-3" *ngIf="form.get('password')?.touched && form.get('password')?.invalid">
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('password')?.errors?.['required']">
                    Password is required.
                  </div>
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('password')?.errors?.['minlength']">
                    Password must be at least 8 characters long.
                  </div>
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('password')?.errors?.['maxlength']">
                    Password must not exceed 20 characters.
                  </div>
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('password')?.errors?.['noLetter']">
                    Password must contain at least one letter.
                  </div>
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('password')?.errors?.['noNumber']">
                    Password must contain at least one number.
                  </div>
                  <div class="ng-invalid pl-1 text-xs text-red-500" *ngIf="form.get('password')?.errors?.['noSpecialChar']">
                    Password must contain at least one special character.
                  </div>
                </div>
                <span class="text-xs text-gray-500">Your password must be 8-20 characters long, contain letters, numbers, and special characters.</span>
              </div>
            </div>

            <!-- Password Confirmation Field -->
            <div>
              <div class="flex justify-start">
                <label for="password_confirmation" class="block text-sm font-medium leading-6 text-gray-900">
                  Password confirmation
                </label>
                <span class="font-semibold text-red-500 pl-1">*</span>
              </div>
              <div class="mt-1">
                <input id="password_confirmation"
                       formControlName="password_confirmation"
                       type="password"
                       (blur)="form.get('password_confirmation')?.markAsTouched()"
                       autocomplete="new-password"
                       required
                       class="input">
              </div>
              <div class="pt-3" *ngIf="form.get('password_confirmation')?.touched && form.errors?.['notMatching']">
                <div class="ng-invalid pl-1 text-xs text-red-500">
                  Passwords do not match.
                </div>
              </div>
            </div>

            <!-- Invitation Code Field -->
            <div>
              <div class="flex justify-start">
                <label for="invitation_code" class="block text-sm font-medium leading-6 text-gray-900">Invitation Code (optional)</label>
              </div>
              <div class="mt-1">
                <input id="invitation_code" formControlName="invitation_code" type="text" autocomplete="off" class="input">
              </div>
            </div>

            <!-- Submit Button -->
            <div class="pt-6">
              <button type="submit"
                      class="w-full btn-auth rounded-lg disabled:opacity-75 disabled:cursor-not-allowed"
                      [disabled]="isLoading || form.invalid">
                <span *ngIf="!isLoading">Sign up</span>
                <span *ngIf="isLoading" class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating account...
                </span>
              </button>
            </div>
          </fieldset>
        </form>

        <!-- Sign In Link -->
        <div class="text-center">
          <p class="text-sm font-medium text-gray-500">
            Already have an account?
            <a routerLink="/login" class="font-medium text-auth hover:opacity-90">
              Sign in here
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</div>
